/**
 * Fixture for the queue widget — shape of list_jobs's structured output
 * (mcp/server.ts listJobsTool → jobView rows, newest first). Includes
 * the contract's "waiting: budget" row — a queued job carrying the
 * phase-9 `holdReason: "budget"` the dispatch gate stamps on held work
 * (the deployed widget groups it under "queued"; the hold reason is on
 * the row data) — and the failed health-sync job whose Retry button
 * exercises the in-place row swap.
 */
import { JOB_IDS, SCHEDULE_IDS, minAgo } from "./seed";

export type QueueFixture = Record<string, unknown> & {
  jobs: Record<string, unknown>[];
};

export function queueFixture(): QueueFixture {
  return {
    jobs: [
      {
        jobId: JOB_IDS.heldBrief,
        agentName: "morning-brief",
        prompt:
          "Compile the morning brief: overnight runs, today's calendar, " +
          "and anything that needs a human.",
        source: "mcp",
        scheduleId: null,
        state: "queued",
        sessionId: null,
        createdAt: minAgo(6),
        deliveredAt: null,
        error: null,
        attempts: 0,
        mode: "message",
        loopInterval: null,
        model: null,
        effort: "medium",
        alwaysRun: false,
        // Phase-9: held by the night budget — waiting, not lost.
        holdReason: "budget",
      },
      {
        jobId: JOB_IDS.queuedSweep,
        agentName: "email-triage",
        prompt: "Re-check the inbox for replies to this morning's drafts.",
        source: "mcp",
        scheduleId: null,
        state: "queued",
        sessionId: null,
        createdAt: minAgo(12),
        deliveredAt: null,
        error: null,
        attempts: 0,
        mode: "message",
        loopInterval: null,
        model: null,
        effort: null,
        alwaysRun: false,
        holdReason: null,
      },
      {
        jobId: JOB_IDS.failedHealth,
        agentName: "health-sync",
        prompt:
          "Pull last night's sleep and recovery data, then update the " +
          "health log and weekly rollup.",
        source: "schedule",
        scheduleId: SCHEDULE_IDS.health,
        state: "failed",
        sessionId: null,
        createdAt: minAgo(230),
        deliveredAt: null,
        error: "machine offline — delivery timed out after 3 attempts",
        attempts: 3,
        mode: "message",
        loopInterval: null,
        model: "haiku",
        effort: "low",
        alwaysRun: true,
        holdReason: null,
      },
      {
        jobId: JOB_IDS.deliveredSweep,
        agentName: "email-triage",
        prompt:
          "Sweep the overnight inbox. Flag anything urgent, draft replies " +
          "for review, and file the rest.",
        source: "schedule",
        scheduleId: SCHEDULE_IDS.sweep,
        state: "delivered",
        sessionId: "ses-4c08-demo",
        createdAt: minAgo(310),
        deliveredAt: minAgo(309),
        error: null,
        attempts: 1,
        mode: "message",
        loopInterval: null,
        model: null,
        effort: null,
        alwaysRun: false,
        holdReason: null,
      },
    ],
    teamRef: null,
  };
}

/**
 * Demo-mode retry_job: mint a fresh queued job (the real tool re-creates
 * rather than mutates — the failed row stays as history), append it to
 * the fixture so refresh/auto-refresh stays coherent, and return the
 * retryJobTool-shaped structured payload the widget's row swap reads.
 */
export function applyDemoRetry(
  fix: QueueFixture,
  jobId: string
): Record<string, unknown> {
  const old = fix.jobs.find((j) => j.jobId === jobId);
  const createdAt = new Date().toISOString();
  const fresh = {
    ...(old ?? {}),
    jobId: `job-retry-${Math.random().toString(36).slice(2, 6)}`,
    source: "mcp",
    state: "queued",
    sessionId: null,
    createdAt,
    deliveredAt: null,
    error: null,
    attempts: 0,
    holdReason: null,
    retryOf: jobId,
  };
  fix.jobs = [fresh, ...fix.jobs];
  return {
    retriedJobId: jobId,
    jobId: fresh.jobId,
    agent: (old?.agentName as string) ?? "health-sync",
    state: "queued",
    sessionId: null,
    mode: (old?.mode as string) ?? "message",
    createdAt,
    teamRef: null,
  };
}
